import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, catchError, filter, map, of, switchMap, timer } from 'rxjs';

import { RolesMenuService } from 'src/app/apps/nodo/modules/administracion/services/roles-menus.service';
import { FacturacionPendienteService } from 'src/app/apps/nodo/modules/facturacion-pendiente/services/facturacion-pendiente.service';
import { IFacturacionPendiente } from 'src/app/apps/nodo/modules/facturacion-pendiente/interfaces/IFacturacionPendiente';
import { NotificacionesVistasService } from 'src/app/shared/services/notificaciones-vistas.service';

/** Intervalo de sondeo (ms). Igual que el monitor de paquetes. */
const POLL_INTERVAL_MS = 60_000;

/** Portal Nodo. */
const PORTAL_NODO = '3';

/** Toda la app autenticada: el aviso debe seguir vivo aunque el usuario cambie de pantalla. */
const PREFIJO_APP = '/app';

/** Ruta del menú que habilita el monitor (solo quien puede ver la pantalla de aprobación). */
const RUTA_MENU_HABILITADOR = 'app/nodo/nomina/facturacion-pendiente';

/**
 * Avisa en la campanita de la facturación pendiente de aprobación (AIU dinámico). Es la
 * mitad "en Nexus" del aviso HAG-152; la otra mitad es el correo (job del backend).
 *
 * Deriva el aviso de estado vivo: la cola de facturación pendiente. El backend solo
 * devuelve la cola a quien tiene el permiso (GetPendientes del RBAC), así que si el rol
 * no aprueba, la consulta no trae nada. El registro de "visto" vive en el backend (origen
 * 'FacturacionPendiente'), por usuario, así que un aviso leído no reaparece al recargar.
 * No sondea en el login: cuelga del effect, que arranca solo con contexto y permiso.
 */
@Injectable({ providedIn: 'root' })
export class FacturacionPendienteMonitorService implements OnDestroy {
    private readonly service = inject(FacturacionPendienteService);
    private readonly rolesMenuService = inject(RolesMenuService);
    private readonly router = inject(Router);
    private readonly vistas = inject(NotificacionesVistasService);

    private readonly enApp = signal<boolean>(false);
    private readonly permiso = signal<boolean | null>(null);
    private permisoEnConsulta = false;

    /** Casos pendientes de aprobación aún no vistos, para la campanita. */
    readonly pendientes = signal<IFacturacionPendiente[]>([]);

    private sub: Subscription | null = null;

    constructor() {
        this.enApp.set(this.esRutaApp(this.router.url));
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => this.enApp.set(this.esRutaApp(e.urlAfterRedirects)));

        effect(() => {
            const enApp = this.enApp();
            const permiso = this.permiso();

            if (!enApp) {
                this.detener();
                return;
            }
            if (permiso === null) {
                this.resolverPermiso();
                this.detener();
                return;
            }
            if (!permiso) {
                this.detener();
                this.pendientes.set([]);
                return;
            }
            this.arrancar();
        });
    }

    ngOnDestroy(): void {
        this.detener();
    }

    /** El usuario abrió el aviso: se registra 'visto' para que no vuelva. */
    marcarVisto(id: number): void {
        this.pendientes.set(this.pendientes().filter((p) => p.id !== id));
        this.vistas.marcarVisto('FacturacionPendiente', String(id)).subscribe();
    }

    private esRutaApp(url: string): boolean {
        return (url ?? '').startsWith(PREFIJO_APP);
    }

    private resolverPermiso(): void {
        if (this.permisoEnConsulta) return;
        this.permisoEnConsulta = true;
        this.rolesMenuService
            .getMenusbyId(PORTAL_NODO)
            .pipe(catchError(() => of(null)))
            .subscribe((resp) => {
                this.permisoEnConsulta = false;
                this.permiso.set(resp ? this.contieneMenuHabilitador(resp.data ?? []) : false);
            });
    }

    private contieneMenuHabilitador(menus: any[]): boolean {
        for (const menu of menus ?? []) {
            const ruta = (menu?.urlRuta ?? '').trim();
            if (ruta === RUTA_MENU_HABILITADOR) return true;
            if (menu?.items?.length && this.contieneMenuHabilitador(menu.items)) return true;
        }
        return false;
    }

    private arrancar(): void {
        if (this.sub) return;
        this.sub = timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.service.getPendientes().pipe(catchError(() => of(null)))),
                switchMap((res) => {
                    if (!res) return of(null);
                    // Mismos criterios que el correo: pendientes reales, sin los sembrados de prueba.
                    const casos = (res.data ?? []).filter((c) => !c.esPrueba);
                    if (casos.length === 0) return of([] as IFacturacionPendiente[]);
                    return this.vistas
                        .pendientes(
                            'FacturacionPendiente',
                            casos.map((c) => ({ clave: String(c.id) }))
                        )
                        .pipe(map((claves) => casos.filter((c) => claves.includes(String(c.id)))));
                })
            )
            .subscribe((casos) => {
                if (!casos) return;
                this.pendientes.set(casos);
            });
    }

    private detener(): void {
        this.sub?.unsubscribe();
        this.sub = null;
    }
}
